/**
 * ReflectionProbe semantic rules: `ambient_color` or `ambient_color_energy` set while
 * `ambient_mode` is not AMBIENT_COLOR (2), an info that the value has no effect, and an
 * `origin_offset` that `size` clamps. Format validation lives in linterParser.ts.
 */
import { ruleRegistry } from '../../../linter/RuleRegistry.js';
import { ruleInt } from '../../../godot/variant/intSlots.js';
import { matchVector3 } from '../../../linter/validators/vectorValidators.js';
import { loadedOriginOffset, REFLECTION_PROBE_DEFAULTS } from '../../../godot/scene/reflectionProbe.js';
import { ReflectionProbeAmbientMode } from '../../../godot/rendering/environment/reflectionProbe.js';
import { formatReal, storedVector3 } from '../../../godot/math/real.js';
import { armDiagnostic, armEmits, groundedArm, reportArm } from '../../../linter/ruleArms.js';
import { VECTOR3_AXES } from '../../../godot/math/vector.js';
const arms = {
    ambientColorNoEffect: groundedArm('reflectionprobe-ambient-color-no-effect', {
        kind: 'engine-inert',
        at: 'scene_forward_lights_inc.glsl:998',
        unused: 'the ambient colour is read only while the mode is AMBIENT_COLOR',
    }),
    originOffsetClamped: {
        severity: 'warning',
        ruleName: 'reflectionprobe-origin-offset-clamped',
        grounding: { kind: 'engine', at: 'reflection_probe.cpp:99-131' },
    },
};
// `_validate_property` (reflection_probe.cpp:203-212) only sets PROPERTY_USAGE_NO_EDITOR,
// which equals PROPERTY_USAGE_STORAGE (object.h:132), so the keys still serialise. The
// class doc's prose is no basis (ADR-0032).
const AMBIENT_ONLY_KEYS = ['ambient_color', 'ambient_color_energy'];
function checkAmbientMode(node, props) {
    // light_storage.cpp:1817 copies `ambient_color` unconditionally, but its only read
    // (scene_forward_lights_inc.glsl:998) runs in `case REFLECTION_AMBIENT_COLOR:` of the
    // switch at scene_forward_lights_inc.glsl:977, in the `renderer_rd` backend of Forward+
    // and Mobile.
    const modeRaw = props.ambient_mode;
    const mode = ruleInt(modeRaw, REFLECTION_PROBE_DEFAULTS.ambientMode);
    if (mode === ReflectionProbeAmbientMode.COLOR)
        return [];
    const diagnostics = [];
    for (const key of AMBIENT_ONLY_KEYS) {
        if (props[key] === undefined)
            continue;
        reportArm(diagnostics, arms.ambientColorNoEffect, node, `ReflectionProbe '${node.name}' sets '${key}' but 'ambient_mode' is not AMBIENT_COLOR (2), so '${key}' has no effect. It still saves and reloads fine; the editor just hides it from the inspector while another ambient mode is selected.`);
    }
    return diagnostics;
}
const formatVector3 = (v) => `Vector3(${VECTOR3_AXES.map((axis) => formatReal(v[axis])).join(', ')})`;
const sameReal = (a, b) => a === b || (Number.isNaN(a) && Number.isNaN(b));
/**
 * The file's `origin_offset` and the one Godot holds after it applies the `size` and
 * `origin_offset` lines in order, or `null` when the offset is absent or either line is malformed.
 */
function replayOriginOffset(rawProps) {
    const lines = [];
    for (const [key, raw] of Object.entries(rawProps)) {
        if (key !== 'size' && key !== 'origin_offset')
            continue;
        const value = matchVector3(raw);
        if (!value)
            return null;
        lines.push([key, value]);
    }
    const written = lines.find(([key]) => key === 'origin_offset')?.[1];
    if (!written)
        return null;
    return {
        written: storedVector3(written),
        loaded: loadedOriginOffset(lines),
    };
}
function checkOriginOffset(node, rawProps) {
    const replayed = replayOriginOffset(rawProps);
    if (!replayed)
        return [];
    const { written, loaded } = replayed;
    if (VECTOR3_AXES.every((axis) => sameReal(written[axis], loaded[axis])))
        return [];
    return [
        armDiagnostic(arms.originOffsetClamped, node, `ReflectionProbe 'origin_offset' ${formatVector3(written)} loads as ${formatVector3(loaded)}: Godot clamps each axis to within half the 'size' less 0.01, against the size in effect when the file lists the offset.`),
    ];
}
function checkReflectionProbe(context) {
    const { node } = context;
    const rawProps = node.rawProperties;
    return [...checkAmbientMode(node, rawProps), ...checkOriginOffset(node, rawProps)];
}
const reflectionProbeValidationRule = {
    meta: {
        name: 'valid-reflectionprobe-properties',
        description: "Flags ambient_color/ambient_color_energy authored while ambient_mode isn't AMBIENT_COLOR (legal and still serialised, but inert), and an origin_offset outside the probe's size, which Godot clamps at load",
        category: 'validation',
        applicableNodeTypes: ['ReflectionProbe'],
        emits: armEmits(arms),
    },
    check: checkReflectionProbe,
};
ruleRegistry.register(reflectionProbeValidationRule);
//# sourceMappingURL=linter.js.map